import axios, { AxiosError } from 'axios';
import create from 'zustand';

import { storage } from '@utils/browser';

import { config } from 'app-config';

import { isInTemplateRoute } from 'ui/router';

import { queryClient } from './query-client';

const instance = axios.create({
  baseURL: config.API_URL,
  xsrfHeaderName: 'X-CSRFToken',
  xsrfCookieName: 'csrftoken',
});

const cancelTokenSource = axios.CancelToken.source();

const getCSRFToken = async (): Promise<string> => {
  const response = await instance.get('v1/auth/csrf');
  return response.headers?.['x-csrftoken'] ?? '';
};

instance.interceptors.request.use(async (config) => {
  const debugToken = storage.get('dbg');
  const isAwsRequest = config.url?.includes('amazonaws.com');

  if (isInTemplateRoute()) {
    const regex = /^v[0-9]\/templates\//;
    const url = config.url ?? '';
    if (
      !regex.test(url) &&
      !config.url?.startsWith('v1/auth') &&
      !config.url?.startsWith('v1/workspaces') &&
      !config.url?.startsWith('v1/marketplace/template')
    ) {
      config.cancelToken = cancelTokenSource.token;
      cancelTokenSource.cancel('Non-template related request cancelled');
    }
  }

  if (isAwsRequest) {
    // We don't want to set credentials: include for AWS blob requests
    // as it will throw CORS errors
    config.withCredentials = false;
  } else if (debugToken) {
    config.headers = { Authorization: `Token ${debugToken}`, ...config.headers };
    config.withCredentials = false;
  } else {
    config.withCredentials = true;
    if (config.method?.toLowerCase() !== 'get') {
      const CSRFToken = await getCSRFToken();
      config.headers = { 'X-CSRFToken': CSRFToken, ...config.headers };
    }
  }

  if (config.responseType === 'blob') {
    config.transformRequest = [
      (data, headers) => {
        headers.common?.delete('Content-Type');
        return data;
      },
    ];
  }

  return config;
});

instance.interceptors.response.use((response) => {
  if (response.status === 401) {
    removeDebugToken();
    queryClient.resetQueries();
  }
  return response;
});

// PR environments: detect sleeping backend and auto-retry with waking UI
export const usePrWaking = create<{ isWaking: boolean }>(() => ({ isWaking: false }));

if (import.meta.env.MODE === 'pr') {
  let wakingPromise: Promise<void> | null = null;

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const waitForWake = (): Promise<void> => {
    if (!wakingPromise) {
      usePrWaking.setState({ isWaking: true });
      wakingPromise = (async () => {
        while (true) {
          await sleep(5000);
          try {
            const response = await axios.get(`${config.API_URL}/health/`, { timeout: 5000 });
            // Ensure we're getting Django's response ("ok"), not the Lambda's
            // {"status": "ready"} — the ALB rule swap needs time to propagate
            if (response.data?.status) {
              continue;
            }
            usePrWaking.setState({ isWaking: false });
            wakingPromise = null;
            return;
          } catch {
            // Still waking, keep polling
          }
        }
      })();
    }
    return wakingPromise;
  };

  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (error.response?.status === 503 && error.response.headers['x-pr-waking']) {
      await waitForWake();
      return instance.request(error.config!);
    }
    return Promise.reject(error);
  });
}

export const request = instance;

export const setDebugToken = (token: string) => {
  storage.set('dbg', token);
};

export const removeDebugToken = () => {
  storage.remove('dbg');
};

export const loadBlob = async (url: string) => {
  const response = await request.get(url, { responseType: 'blob' });
  return response.data;
};
